// ./src/utils/api/CouponService.ts
//
// Issued, scannable coupons for an event (given to guests, RSVP groups, or
// walk-ins). OpResult-native. Account-owned: reads are account-scoped and only
// the caller's own coupons are editable (see `rights`). A multi-id update/remove
// is a bulk op (server restricts a bulk update to lifecycle/status). Generation
// is done by issuing N couponCreate calls (see useCoupons.generate). See
// em-scaffold-ui.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import type { EventTimeSlot } from "./EventTimeSlotService";
import { listAllPages, ListAllOptions } from "./listAllPages";
import { Pagination, SortItem } from "./commonApiTypes";
import { CouponTypeItem } from "./CouponTypeService";
import type { ServicePointRef } from "./ServicePointService";

// =============================================================================
// GraphQL
// =============================================================================

const COUPON_FIELDS = `
  id
  accountId
  couponTypeId
  eventId
  eventGuestId
  rsvpGroupId
  issuedToLabel
  code
  sequenceNo
  issueSource
  redeemedCount
  couponStatus
  notes
  voidedAt
  voidedByUserId
  voidedByEventGuestId
  voidedByLabel
  voidReason
  eventTimeSlotId
  eventTimeSlot { id name startsAt endsAt isAllDay }
  status
  rights
  createdAt
  couponType {
    id
    name
    color
    icon
    couponValue
    maxRedemptions
    items { id name quantity unitCost currencyCode isOptional }
  }
  eventGuest {
    id
    firstName
    lastName
    person { firstName lastName }
  }
  rsvpGroup { id name }
  redemptions {
    id
    redeemedAt
    redeemedByUserId
    redeemedByEventGuestId
    redeemedByLabel
    servicePoint { id name kind removedAt }
    dateOverride
    items { id name quantity unitCost }
  }
`;

const COUPON_LIST_QUERY = gql`
  query CouponList($filter: CouponFilter, $params: PaginationAndSorting) {
    couponList(filter: $filter, params: $params) {
      code
      total
      data { ${COUPON_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const COUPON_SUMMARY_QUERY = gql`
  query CouponEventSummary($filter: CouponFilter) {
    couponEventSummary(filter: $filter) {
      code
      data { totalCoupons redeemedCoupons owedAmount potentialAmount currencyCode }
      errors { name errors warnings }
    }
  }
`;

const COUPON_GET_QUERY = gql`
  query CouponGet($id: ID) {
    couponGet(id: $id) {
      code
      data { ${COUPON_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const COUPON_CREATE_MUTATION = gql`
  mutation CouponCreate($params: CouponInput) {
    couponCreate(params: $params) {
      code
      data { ${COUPON_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const COUPON_UPDATE_MUTATION = gql`
  mutation CouponUpdate($where: CouponWhereInput, $params: CouponInput) {
    couponUpdate(where: $where, params: $params) {
      code
      data { ${COUPON_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const COUPON_REMOVE_MUTATION = gql`
  mutation CouponRemove($where: CouponWhereInput) {
    couponRemove(where: $where) {
      code
      data { id }
      errors { name errors warnings }
    }
  }
`;

const COUPON_GENERATE_MUTATION = gql`
  mutation CouponGenerate($params: CouponGenerateInput) {
    couponGenerate(params: $params) {
      code
      data { ${COUPON_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const COUPON_GENERATE_FROM_RULES_MUTATION = gql`
  mutation CouponGenerateFromRules($params: CouponGenerateFromRulesInput) {
    couponGenerateFromRules(params: $params) {
      code
      data {
        created
        alreadyGranted
        rulesApplied
        lines { couponTypeId couponTypeName created alreadyGranted }
      }
      errors { name errors warnings }
    }
  }
`;

const COUPON_REDEEM_MUTATION = gql`
  mutation CouponRedeem($where: CouponWhereInput, $params: CouponRedeemInput) {
    couponRedeem(where: $where, params: $params) {
      code
      data { ${COUPON_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const COUPON_UNREDEEM_MUTATION = gql`
  mutation CouponUnredeem($where: CouponWhereInput) {
    couponUnredeem(where: $where) {
      code
      data { ${COUPON_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

// =============================================================================
// Types (match the eventsmagic-api Coupon schema)
// =============================================================================

export interface CouponRedemptionItemLite {
  id?: string;
  name?: string | null;
  quantity?: number | null;
  unitCost?: number | null;
}

export interface CouponRedemptionLite {
  id?: string;
  redeemedAt?: string | null;
  /** Null when a crew member redeemed via the password link. */
  redeemedByUserId?: string | null;
  /** Crew/vendor staff who redeemed (event_guest). */
  redeemedByEventGuestId?: string | null;
  /** Name snapshot of whoever redeemed (display only; the ids are authoritative). */
  redeemedByLabel?: string | null;
  /** #169 — the stall / bar it was redeemed at (null = not recorded). */
  servicePoint?: ServicePointRef | null;
  /** ROADMAP A4: crew redeemed it on a date the coupon is not for. */
  dateOverride?: boolean | null;
  items?: CouponRedemptionItemLite[] | null;
}

export interface CouponTypeLite {
  id?: string;
  name?: string | null;
  color?: string | null;
  icon?: string | null;
  couponValue?: number | null;
  maxRedemptions?: number | null;
  items?: CouponTypeItem[] | null;
}

export interface CouponGuestLite {
  id?: string;
  firstName?: string | null;
  lastName?: string | null;
  person?: { firstName?: string | null; lastName?: string | null } | null;
}

export interface CouponGroupLite {
  id?: string;
  name?: string | null;
}

export interface Coupon {
  id?: string;
  accountId?: string | null;
  couponTypeId?: string | null;
  eventId?: string | null;
  /** Guest it was given to (null = group / walk-in). */
  eventGuestId?: string | null;
  /** RSVP group it was given to (null = individual guest / walk-in). */
  rsvpGroupId?: string | null;
  /** Walk-in / volunteer name when no guest or group link. */
  issuedToLabel?: string | null;
  code?: string | null;
  /** A4b — the human number on the printed voucher, unique per event and voucher type. Read-only. */
  sequenceNo?: number | null;
  /** rsvp | purchase | manual | batch. */
  issueSource?: string | null;
  redeemedCount?: number | null;
  /** issued | redeemed | void | expired. */
  couponStatus?: string | null;
  notes?: string | null;
  voidedAt?: string | null;
  voidedByUserId?: string | null;
  voidedByEventGuestId?: string | null;
  /** Name snapshot of whoever voided it (display only; the ids are authoritative). */
  voidedByLabel?: string | null;
  /** Why the SYSTEM voided it (COUPON_VOID_REASON); null = a person did, or not void. */
  voidReason?: string | null;
  /** ROADMAP A4: the date this coupon may be redeemed on. Null = any date. */
  eventTimeSlotId?: string | null;
  /** That date, as the stall shows it (2026-10-18 — read with the voucher: a voucher redeemer may not list the schedule). */
  eventTimeSlot?: Pick<EventTimeSlot, "id" | "name" | "startsAt" | "endsAt" | "isAllDay"> | null;
  status?: number;
  rights?: string[] | null;
  createdAt?: string;
  couponType?: CouponTypeLite | null;
  eventGuest?: CouponGuestLite | null;
  rsvpGroup?: CouponGroupLite | null;
  redemptions?: CouponRedemptionLite[] | null;
}

export interface CouponInput {
  /** Required on create, create-only. */
  eventId?: string | null;
  /** Required on create, create-only. */
  couponTypeId?: string | null;
  eventGuestId?: string | null;
  rsvpGroupId?: string | null;
  issuedToLabel?: string | null;
  issueSource?: string | null;
  couponStatus?: string | null;
  status?: number | null;
  notes?: string | null;
  /** Create-only (ROADMAP A4): the date this coupon may be redeemed on. Null = any date. */
  eventTimeSlotId?: string | null;
}

// A bulk update (>1 id) may only mass-change couponStatus / status (server-enforced).
export interface CouponBulkInput {
  couponStatus?: string | null;
  status?: number | null;
}

/** One line item the guest actually took at redemption — a menu item (couponTypeItemId) or an ad-hoc swap. */
export interface CouponRedeemItemInput {
  /** The coupon_type_item taken. Omit for an ad-hoc swap; then `name` is required. */
  couponTypeItemId?: string | null;
  name?: string | null;
  /** How many taken (default 1). 0 = not taken (dropped server-side). */
  quantity?: number | null;
  unitCost?: number | null;
  currencyCode?: string | null;
}

export interface CouponFilter {
  id?: string[] | null;
  eventId?: string[] | null;
  couponTypeId?: string[] | null;
  eventTimeSlotId?: string[] | null;
  eventGuestId?: string[] | null;
  rsvpGroupId?: string[] | null;
  couponStatus?: string[] | null;
  status?: number[] | null;
  code?: string[] | null;
  /** walkin | guest | group. */
  assigneeKind?: string | null;
  /** Matches the coupon code or walk-in label. */
  searchKeyword?: string | null;
}

export interface CouponEventSummary {
  totalCoupons?: number | null;
  redeemedCoupons?: number | null;
  owedAmount?: number | null;
  potentialAmount?: number | null;
  /** The event's currency (#180b — the dashboard card printed every amount in USD). */
  currencyCode?: string | null;
}

/** What one voucher got out of a grant run. */
export interface CouponGrantLine {
  couponTypeId?: string | null;
  couponTypeName?: string | null;
  created?: number | null;
  alreadyGranted?: number | null;
}

/** Outcome of running an event's RSVP grant rules. */
export interface CouponGrantSummary {
  created?: number | null;
  alreadyGranted?: number | null;
  rulesApplied?: number | null;
  lines?: CouponGrantLine[] | null;
}

export interface CouponWhereInput {
  /** One id, or many for a bulk update/remove. */
  id?: string | string[];
}

export interface CouponGenerateInput {
  eventId: string;
  couponTypeId: string;
  quantity: number;
  eventGuestId?: string | null;
  rsvpGroupId?: string | null;
  issuedToLabel?: string | null;
  issueSource?: string | null;
  notes?: string | null;
  /** ROADMAP A4: the date every issued coupon may be redeemed on. Null = any date. */
  eventTimeSlotId?: string | null;
}

// The lifecycle values (mirror the API COUPON_STATUS boundary).
export const COUPON_STATUS = {
  ISSUED: "issued",
  REDEEMED: "redeemed",
  VOID: "void",
  EXPIRED: "expired",
} as const;

/** Why the SYSTEM voided a coupon (mirrors the API COUPON_VOID_REASON). */
export const COUPON_VOID_REASON = {
  /** ROADMAP A4: the guest un-booked the date it was for. */
  DATE_DROPPED: "date_dropped",
} as const;

// =============================================================================
// Reconciliation helper
// =============================================================================

/**
 * The default cost of one redemption of a coupon type: the summed cost of its
 * priced items, or its flat coupon value when the items carry no cost.
 */
export const couponTypeCost = (type?: CouponTypeLite | null): number => {
  if (!type) {
    return 0;
  }

  const itemsTotal = (type.items ?? []).reduce((s, i) => s + (i.unitCost ?? 0) * (i.quantity ?? 1), 0);

  return itemsTotal > 0 ? itemsTotal : type.couponValue ?? 0;
};

/**
 * What is owed to the vendor for a single coupon: the sum of its recorded
 * redemptions' item costs (flat value fallback per redemption), or — when it was
 * simply marked redeemed with no detailed scan — the coupon type's default cost.
 * Unredeemed coupons owe nothing.
 */
export const couponOwed = (coupon: Coupon): number => {
  const redemptions = coupon.redemptions ?? [];
  const typeCost = couponTypeCost(coupon.couponType);

  if (redemptions.length > 0) {
    return redemptions.reduce((total, r) => {
      const items = r.items ?? [];

      if (items.length > 0) {
        return total + items.reduce((s, i) => s + (i.unitCost ?? 0) * (i.quantity ?? 1), 0);
      }

      return total + typeCost;
    }, 0);
  }

  // No detailed redemption records — but a "marked redeemed" coupon still owes.
  if (coupon.couponStatus === COUPON_STATUS.REDEEMED) {
    return typeCost;
  }

  return 0;
};

// =============================================================================
// Service
// =============================================================================

export class CouponService {
  static async list(filter: CouponFilter, pagination?: Pagination, sortBy?: SortItem[]): Promise<OpResult> {
    return ApiService.executeQuery(COUPON_LIST_QUERY, {
      filter: filter || {},
      // No page given is no cap — not a silent 50 (ROADMAP #118; every other service sends none either).
      params: { pagination: pagination || undefined, sortBy: sortBy || undefined },
    });
  }

  /** Owed / potential reconciliation totals for an event (respects the type / assignee filter). */
  static async summary(filter: CouponFilter): Promise<OpResult> {
    return ApiService.executeQuery(COUPON_SUMMARY_QUERY, { filter: filter || {} });
  }

  /**
   * Fetches every coupon matching `filter` by paging through the list — for CSV
   * export, which must cover the whole filtered set, not just the loaded page.
   */
  static async listAll(
    filter: CouponFilter,
    sortBy?: SortItem[],
    opts?: ListAllOptions
  ): Promise<OpResult> {
    return listAllPages<Coupon>(CouponService.list, filter, sortBy, opts);
  }

  static async get(id: string): Promise<OpResult> {
    return ApiService.executeQuery(COUPON_GET_QUERY, { id });
  }

  static async create(params: CouponInput): Promise<OpResult> {
    return ApiService.executeMutation(COUPON_CREATE_MUTATION, { params });
  }

  static async update(idOrIds: string | string[], params: CouponInput | CouponBulkInput): Promise<OpResult> {
    return ApiService.executeMutation(COUPON_UPDATE_MUTATION, { where: { id: idOrIds }, params });
  }

  static async remove(idOrIds: string | string[]): Promise<OpResult> {
    return ApiService.executeMutation(COUPON_REMOVE_MUTATION, { where: { id: idOrIds } });
  }

  /** Issue a batch of identical coupons for an event in one call (server-side). */
  static async generate(params: CouponGenerateInput): Promise<OpResult> {
    return ApiService.executeMutation(COUPON_GENERATE_MUTATION, { params });
  }

  /**
   * Issue the event's RSVP grant rules to everyone attending. Idempotent — only
   * the shortfall is created, so it's safe to re-run as more guests RSVP.
   */
  static async generateFromRules(eventId: string): Promise<OpResult> {
    return ApiService.executeMutation(COUPON_GENERATE_FROM_RULES_MUTATION, { params: { eventId } });
  }

  /**
   * Record ONE attributed redemption of each coupon (server stamps who + when). `items` records WHICH
   * line items the guest actually took (a subset / swapped / ad-hoc) — omit for a bare redemption.
   */
  static async redeem(
    idOrIds: string | string[],
    notes?: string | null,
    items?: CouponRedeemItemInput[] | null,
    opts: { overrideDate?: boolean; servicePointId?: string | null } = {},
  ): Promise<OpResult> {
    return ApiService.executeMutation(COUPON_REDEEM_MUTATION, {
      where: { id: idOrIds },
      params: {
        notes: notes ?? null,
        // #169 — the stall / bar the redemption happens at.
        ...(opts.servicePointId ? { servicePointId: opts.servicePointId } : {}),
        ...(items && items.length > 0 ? { items } : {}),
        // ROADMAP A4 — redeem a coupon for another date anyway; the server records the override.
        ...(opts.overrideDate === true ? { overrideDate: true } : {}),
      },
    });
  }

  /** Undo the most recent redemption of each coupon. */
  static async unredeem(idOrIds: string | string[]): Promise<OpResult> {
    return ApiService.executeMutation(COUPON_UNREDEEM_MUTATION, { where: { id: idOrIds } });
  }
}
